import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../api';
import { BLOCK_KINDS, BlockEditor, blockLabel, isFixed } from '../components/NewsletterBlocks';
import { ErrorNotice, Notice, Spinner, formatDate } from '../components/ui';
import type {
  Newsletter,
  NewsletterBlock,
  NewsletterBlockContent,
  NewsletterImage,
  NewsletterImageList,
  NewsletterPreview,
} from '../types';

/**
 * The newsletter composer.
 *
 * Two decisions shape this screen.
 *
 * **The preview is the real thing.** It is not drawn here — the blocks are
 * posted to the server, which renders them with the same pure function the
 * send will use, and the result is shown in a sandboxed iframe. If this page
 * drew its own approximation, what somebody approved would not be what went
 * out, and the two would drift apart without anybody noticing until a hundred
 * and fifty people had already received the difference.
 *
 * **One Save, pressed on purpose.** Not autosave: a volunteer who has made a
 * mess of a newsletter needs to be able to walk away from it, and a screen
 * that has already written everything down takes that away. The cost is that
 * work can be lost, so the button says when it last saved, and leaving with
 * unsaved changes asks first.
 */
/**
 * A block while it is being edited.
 *
 * The key is this screen's own and never leaves it. React needs something
 * stable to identify a row by, and neither of the obvious candidates works: an
 * index changes the moment a block moves, and a new block has no id until it
 * has been saved. Keying on the object itself is worse still — editing a
 * field replaces it, so the input would be rebuilt on every keystroke and the
 * cursor would jump out of it mid-word.
 */
interface EditableBlock extends NewsletterBlock {
  key: string;
}

/**
 * The rendered email, wrapped in just enough document to sit in an iframe.
 *
 * What the server returns is an email body — a table, no <html> around it,
 * because that is the shape an email is. A browser given that fragment applies
 * its own 8px body margin and white background, which leaves the newsletter
 * floating in a white border it will not have in anybody's inbox. Two lines of
 * reset make the preview show what a mail client shows.
 *
 * The content is server-rendered and already escaped, and the iframe is
 * sandboxed with nothing allowed, so nothing in here can run.
 */
function documentFor(html: string): string {
  return `<!doctype html><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>html,body{margin:0;padding:0;background:#f6f7f9;}</style>
${html}`;
}

export function NewsletterPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const newsletterId = Number(id);

  const [newsletter, setNewsletter] = useState<Newsletter | null>(null);
  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [blocks, setBlocks] = useState<EditableBlock[]>([]);
  const [images, setImages] = useState<NewsletterImage[]>([]);

  const [preview, setPreview] = useState<NewsletterPreview | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const readOnly = newsletter?.status === 'sent';

  // Counts new blocks within this visit, for the keys below.
  const nextKey = useRef(1);

  const adopt = useCallback((loaded: Newsletter) => {
    setNewsletter(loaded);
    setTitle(loaded.title);
    setSubject(loaded.subject);
    setBlocks(loaded.blocks.map((block) => ({ ...block, key: `b${block.id}` })));
    setDirty(false);
  }, []);

  useEffect(() => {
    if (!Number.isInteger(newsletterId) || newsletterId <= 0) {
      setError(new ApiError(404, 'not_found', 'That newsletter could not be found.'));
      return;
    }
    api
      .get<{ newsletter: Newsletter }>(`/newsletters/${newsletterId}`)
      .then((r) => adopt(r.newsletter))
      .catch(setError);

    api
      .get<NewsletterImageList>('/newsletter/images')
      .then((r) => setImages(r.images))
      .catch(() => undefined);
  }, [newsletterId, adopt]);

  // Re-render as the wording changes, a beat behind the typing. The delay is
  // what keeps this to one request a sentence rather than one a keystroke.
  useEffect(() => {
    if (newsletter === null) return;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      api
        .post<NewsletterPreview>(`/newsletters/${newsletterId}/preview`, {
          blocks: forServer(blocks),
        })
        .then((result) => {
          if (!cancelled) setPreview(result);
        })
        .catch(() => undefined);
    }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [blocks, newsletterId, newsletter]);

  // The browser's own "leave site?" prompt. Deliberately the browser's and not
  // ours: it is the one people already recognise, and it cannot be missed.
  useEffect(() => {
    if (!dirty) return;

    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);

    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  function change(next: EditableBlock[]) {
    setBlocks(next);
    setDirty(true);
  }

  /** Without the screen's own key, which the server has no use for. */
  const forServer = (list: EditableBlock[]): NewsletterBlock[] =>
    list.map(({ key, ...block }) => block);

  function editBlock(index: number, content: NewsletterBlockContent) {
    change(blocks.map((block, i) => (i === index ? { ...block, content } : block)));
  }

  function move(index: number, by: number) {
    const to = index + by;
    // The header and footer sit at the ends and stay there, so nothing may
    // step onto or past them.
    if (to < 1 || to > blocks.length - 2) return;

    const next = [...blocks];
    [next[index], next[to]] = [next[to], next[index]];
    change(next);
  }

  function remove(index: number) {
    change(blocks.filter((_, i) => i !== index));
  }

  function add(type: (typeof BLOCK_KINDS)[number]) {
    // Above the footer, which is always last.
    const next = [...blocks];
    next.splice(Math.max(1, next.length - 1), 0, {
      key: `n${nextKey.current++}`,
      id: null,
      type: type.type,
      content: { ...type.blank },
    });
    change(next);
  }

  async function save() {
    setError(null);
    setSaving(true);
    try {
      const result = await api.put<{ newsletter: Newsletter }>(`/newsletters/${newsletterId}`, {
        title,
        subject,
        blocks: forServer(blocks),
      });
      adopt(result.newsletter);
      setSavedAt(new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
    } catch (failure) {
      setError(failure);
    } finally {
      setSaving(false);
    }
  }

  async function discard() {
    if (!window.confirm('Delete this newsletter? Everything written in it goes with it.')) return;
    try {
      await api.del(`/newsletters/${newsletterId}`);
      navigate('/email/newsletters');
    } catch (failure) {
      setError(failure);
    }
  }

  const warnings = preview?.warnings ?? [];

  // Memoised so the iframe is not torn down and rebuilt on every render of
  // the page around it, which would flash white each time.
  const frame = useMemo(
    () => (
      <iframe
        className="newsletter-frame"
        title="How this newsletter will look"
        // Nothing in a newsletter should ever run, and this page is behind a
        // login: an empty sandbox means the preview cannot reach it. It still
        // loads pictures, which is the one thing it does need to do.
        sandbox=""
        srcDoc={documentFor(preview?.html ?? '')}
      />
    ),
    [preview?.html],
  );

  if (newsletter === null) {
    return (
      <>
        <p className="muted small">
          <Link to="/email/newsletters">← Back to newsletters</Link>
        </p>
        <ErrorNotice error={error} />
        {error === null ? <Spinner label="Opening the newsletter" /> : null}
      </>
    );
  }

  return (
    <>
      <p className="muted small">
        <Link to="/email/newsletters">← Back to newsletters</Link>
      </p>

      <div className="page-head">
        <h1>{newsletter.title}</h1>
        <div className="actions">
          {readOnly ? null : (
            <>
              <button type="button" className="primary" disabled={saving || !dirty} onClick={save}>
                {saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
              </button>
              {/*
                Disabled while there are unsaved changes, because the send
                renders what is stored — not what is on this screen. Sending a
                version of the newsletter the writer cannot see would be the
                worst kind of surprise.
              */}
              <Link
                to={dirty ? '#' : `/email/newsletters/${newsletterId}/send`}
                className={dirty ? 'button-link disabled' : 'button-link'}
                aria-disabled={dirty}
                onClick={(e) => {
                  if (dirty) e.preventDefault();
                }}
              >
                {dirty ? 'Save before sending' : 'Send this newsletter'}
              </Link>
            </>
          )}
        </div>
      </div>

      <ErrorNotice error={error} />

      {readOnly ? (
        <Notice kind="warning">
          This went out on {formatDate(newsletter.sent_at)} and cannot be changed. The link inside
          it still opens this newsletter for everybody who received it, so editing it now would
          make that page something other than what they were sent. Start a new one instead.
        </Notice>
      ) : null}

      {savedAt !== null && !dirty ? <Notice kind="success">Saved at {savedAt}.</Notice> : null}

      <div className="composer">
        <div className="composer-blocks">
          <div className="card">
            <div className="field">
              <label htmlFor="nl-title">A name for this newsletter</label>
              <input
                id="nl-title"
                value={title}
                disabled={readOnly}
                aria-describedby="nl-title-hint"
                onChange={(e) => {
                  setTitle(e.target.value);
                  setDirty(true);
                }}
              />
              <span className="hint" id="nl-title-hint">
                Only the committee sees this. It is how you will find it again.
              </span>
            </div>

            <div className="field">
              <label htmlFor="nl-subject">The subject line members will see</label>
              <input
                id="nl-subject"
                value={subject}
                disabled={readOnly}
                aria-describedby="nl-subject-hint"
                onChange={(e) => {
                  setSubject(e.target.value);
                  setDirty(true);
                }}
              />
              <span className="hint" id="nl-subject-hint">
                This is the line in somebody's inbox, so it is worth more thought than the rest.
              </span>
            </div>
          </div>

          {blocks.map((block, index) => (
            <section className="card block" key={block.key}>
              <div className="block-head">
                <h2>{blockLabel(block.type)}</h2>
                {isFixed(block.type) || readOnly ? (
                  <span className="hint">
                    {isFixed(block.type) ? 'Always here' : null}
                  </span>
                ) : (
                  <div className="block-buttons">
                    <button
                      type="button"
                      aria-label={`Move the ${blockLabel(block.type).toLowerCase()} up`}
                      disabled={index <= 1}
                      onClick={() => move(index, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      aria-label={`Move the ${blockLabel(block.type).toLowerCase()} down`}
                      disabled={index >= blocks.length - 2}
                      onClick={() => move(index, 1)}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="link danger"
                      onClick={() => remove(index)}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>

              <fieldset disabled={readOnly}>
                <BlockEditor
                  uid={block.key}
                  type={block.type}
                  content={block.content}
                  images={images}
                  onChange={(content) => editBlock(index, content)}
                />
              </fieldset>
            </section>
          ))}

          {readOnly ? null : (
            <div className="card">
              <h2>Add something</h2>
              <p className="hint">It goes on the end. Move it with the arrows once it is there.</p>
              <ul className="add-blocks">
                {BLOCK_KINDS.map((kind) => (
                  <li key={kind.type}>
                    <button type="button" onClick={() => add(kind)}>
                      <strong>{kind.label}</strong>
                      <span>{kind.hint}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {readOnly ? null : (
            <div className="card">
              <h2>Finished with it?</h2>
              <p className="hint">
                A draft nobody wants can be deleted. Once it has been sent it cannot.
              </p>
              <div className="actions">
                <button type="button" className="link danger" onClick={discard}>
                  Delete this newsletter
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="composer-preview">
          <div className="preview-head">
            <h2>How it will look</h2>
            {dirty ? <span className="hint">Unsaved changes</span> : null}
          </div>

          {warnings.map((warning) => (
            <Notice kind="warning" key={warning}>
              {warning}
            </Notice>
          ))}

          {frame}

          <p className="hint">
            This is the real thing — the same rendering that will be emailed, not an impression of
            it. Names like <code>{'{{first_name}}'}</code> are filled in for each member when it
            goes out.
          </p>
        </div>
      </div>
    </>
  );
}
